import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isEditorIsNestedEditor } from '@lexical/utils';
import useModal from '../../hooks/useModal';
import DropdownColorPicker from '../../ui/DropdownColorPicker';
import { CODE_LANGUAGE_FRIENDLY_NAME_MAP, getLanguageFriendlyName } from '@lexical/code';
import DropDown, { DropDownItem } from '../../ui/DropDown';
import { BlockFormatDropDown, blockTypeToBlockName } from './components/BlockFormatDropDown';
import { IS_APPLE } from '../../utils/environment';
import { useEditorState } from '../../BaseEditor/hooks/useBaseEditorFormatState';
import { dropDownActiveClass, FontDropDown } from './components/FrontDropdown';
import { AdditionalFormatButtons, FormatButtons } from './components/FormatButtons';
import { InsertDropdown } from './components/InsertDropdown';
import { ElementFormatDropdown } from './components/ElementFormatDropdown';

function Divider() {
  return <div className="divider" />;
}

export default function ArticleToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [activeEditor] = useState(editor);
  const [modal, showModal] = useModal();
  const [isImageCaption, setIsImageCaption] = useState(false);

  const {
    isBold,
    isItalic,
    isUnderline,
    isStrikethrough,
    isSubscript,
    isSuperscript,
    isCode,
    isLink,
    blockType,
    elementFormat,
    isRTL,
    fontSize,
    fontColor,
    bgColor,
    fontFamily,
    canUndo,
    canRedo,
    isEditable,
    codeLanguage,
    setFontColor,
    toggleCode,
    toggleLink,
    applyStyleText,
    clearFormatting,
    undo,
    redo,
    applyCodeLanguage
  } = useEditorState(activeEditor);

  useEffect(() => {
    if (activeEditor !== editor && $isEditorIsNestedEditor(activeEditor)) {
      const rootElement = activeEditor.getRootElement();
      setIsImageCaption(
        !!rootElement?.parentElement?.classList.contains('image-caption-container')
      );
    } else {
      setIsImageCaption(false);
    }
  }, [activeEditor, editor]);

  const onFontColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      setFontColor(value);
      applyStyleText({ color: value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const onBgColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ 'background-color': value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const canViewerSeeInsertDropdown = !isImageCaption;
  const canViewerSeeInsertCodeButton = !isImageCaption;

  return (
    <div className={`toolbar no-scrollbar top-[68px] md:top-0 ${isEditable ? '!flex' : '!hidden'}`}>
      <button
        disabled={!canUndo || !isEditable}
        onClick={undo}
        title={IS_APPLE ? 'Undo (⌘Z)' : 'Undo (Ctrl+Z)'}
        type="button"
        className="toolbar-item spaced"
        aria-label="Undo"
      >
        <i className="format undo" />
      </button>
      <button
        disabled={!canRedo || !isEditable}
        onClick={redo}
        title={IS_APPLE ? 'Redo (⌘Y)' : 'Redo (Ctrl+Y)'}
        type="button"
        className="toolbar-item"
        aria-label="Redo"
      >
        <i className="format redo" />
      </button>

      <Divider />

      {blockType === 'code' ? (
        <DropDown
          disabled={!isEditable}
          buttonClassName="toolbar-item code-language"
          buttonLabel={getLanguageFriendlyName(codeLanguage)}
          buttonAriaLabel="Select language"
        >
          {Object.entries(CODE_LANGUAGE_FRIENDLY_NAME_MAP).map(([lang, friendlyName]) => (
            <DropDownItem
              key={lang}
              className={`item ${dropDownActiveClass(lang === codeLanguage)}`}
              onClick={() => applyCodeLanguage(lang)}
            >
              <span className="text">{friendlyName}</span>
            </DropDownItem>
          ))}
        </DropDown>
      ) : (
        <>
          {canViewerSeeInsertDropdown && (
            <>
              <InsertDropdown editor={activeEditor} isEditable={isEditable} showModal={showModal} />
            </>
          )}
          <BlockFormatDropDown
            disabled={!isEditable}
            blockType={blockType as keyof typeof blockTypeToBlockName}
            editor={activeEditor}
          />
          <Divider />

          {/* Font family selector disabled - using consistent font system */}
          {/* <FontDropDown
            disabled={!isEditable}
            style="font-family"
            value={fontFamily}
            editor={activeEditor}
          />
          <Divider /> */}
          <FontDropDown
            disabled={!isEditable}
            style="font-size"
            value={fontSize}
            editor={activeEditor}
          />
          <Divider />

          <FormatButtons
            editor={activeEditor}
            isEditable={isEditable}
            formatState={{
              isBold,
              isItalic,
              isUnderline,
              isStrikethrough,
              isSubscript,
              isSuperscript,
              isCode
            }}
          />

          {canViewerSeeInsertCodeButton && (
            <button
              disabled={!isEditable}
              onClick={toggleCode}
              className={'toolbar-item spaced ' + (isCode ? 'active' : '')}
              title="Insert code block"
              type="button"
              aria-label="Insert code block"
            >
              <i className="format code" />
            </button>
          )}
          <button
            disabled={!isEditable}
            onClick={() => toggleLink()}
            className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
            aria-label="Insert link"
            title="Insert link"
            type="button"
          >
            <i className="format link" />
          </button>

          <DropdownColorPicker
            disabled={!isEditable}
            buttonClassName="toolbar-item color-picker"
            buttonAriaLabel="Formatting text color"
            buttonIconClassName="icon font-color"
            color={fontColor}
            onChange={onFontColorSelect}
            title="text color"
          />
          <DropdownColorPicker
            disabled={!isEditable}
            buttonClassName="toolbar-item color-picker"
            buttonAriaLabel="Formatting background color"
            buttonIconClassName="icon bg-color"
            color={bgColor}
            onChange={onBgColorSelect}
            title="bg color"
          />

          <AdditionalFormatButtons
            editor={activeEditor}
            isEditable={isEditable}
            formatState={{
              isStrikethrough,
              isSubscript,
              isSuperscript
            }}
            clearFormatting={clearFormatting}
          />
          <Divider />
          <ElementFormatDropdown
            disabled={!isEditable}
            value={elementFormat}
            editor={activeEditor}
            isRTL={isRTL}
          />
        </>
      )}
      {modal}
    </div>
  );
}
